// src/components/ResultsDisplay.js
import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Box, Typography, Divider, Grid } from '@mui/material';
import CountUp from 'react-countup';

// Animated stat card component
const StatCard = ({ label, value, suffix = '', prefix = '', color, delay = 0, isLarge = false }) => (
  <motion.div
    initial={{ opacity: 0, y: 20, scale: 0.9 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    transition={{ duration: 0.5, delay }}
  >
    <Box sx={{ 
      textAlign: 'center',
      p: 3,
      borderRadius: 3,
      background: `linear-gradient(135deg, ${color}10 0%, ${color}05 100%)`,
      border: `1px solid ${color}20`,
      position: 'relative',
      overflow: 'hidden',
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: `0 10px 30px ${color}20`,
      },
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '3px',
        background: `linear-gradient(90deg, ${color} 0%, ${color}80 100%)`,
      }
    }}>
      <Typography 
        variant={isLarge ? 'h2' : 'h3'} 
        sx={{ 
          fontWeight: 800, 
          color: color,
          mb: 1,
          fontFamily: '"Outfit", sans-serif',
          letterSpacing: '-0.02em'
        }}
      >
        {prefix}
        <CountUp 
          end={parseFloat(value) || 0} 
          duration={2} 
          separator="," 
          decimals={value.toString().includes('.') ? 1 : 0}
        />
        {suffix}
      </Typography>
      <Typography 
        variant="body2" 
        sx={{ 
          color: '#5a6775',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          fontSize: '0.75rem'
        }}
      >
        {label}
      </Typography>
    </Box>
  </motion.div>
);

// Metric row component
const MetricRow = ({ label, value, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, x: -20 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ duration: 0.4, delay }}
  >
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center',
      py: 2,
      px: 2,
      borderRadius: 2,
      transition: 'all 0.3s ease',
      '&:hover': {
        bgcolor: 'rgba(244, 114, 22, 0.05)',
      }
    }}>
      <Typography variant="body1" sx={{ color: '#5a6775', fontWeight: 500 }}>
        {label}
      </Typography>
      <Typography 
        variant="body1" 
        sx={{ 
          fontWeight: 700,
          color: '#db1d2b',
          fontFamily: '"JetBrains Mono", monospace'
        }}
      >
        {value}
      </Typography>
    </Box>
    <Divider sx={{ opacity: 0.1 }} />
  </motion.div>
);

function ResultsDisplay({ data }) {
  if (!data) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <Box sx={{ 
          p: 6, 
          borderRadius: 4, 
          height: '100%', 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center', 
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
          border: '2px dashed rgba(244, 114, 22, 0.3)',
          position: 'relative',
          overflow: 'hidden',
          minHeight: '400px'
        }}>
          {/* Animated background elements */}
          <Box sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: 'linear-gradient(rgba(244, 114, 22, 0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(244, 114, 22, 0.03) 1px, transparent 1px)',
            backgroundSize: '30px 30px',
          }} />
          
          <motion.div
            animate={{ 
              scale: [1, 1.1, 1],
              opacity: [0.3, 0.5, 0.3]
            }}
            transition={{ 
              duration: 3, 
              repeat: Infinity,
              ease: 'easeInOut'
            }}
            style={{
              position: 'absolute',
              width: '200px',
              height: '200px',
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(244, 114, 22, 0.1) 0%, transparent 70%)',
            }}
          />
          
          <motion.div
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          >
            <Box sx={{ 
              fontSize: '4rem', 
              mb: 3,
              filter: 'drop-shadow(0 4px 20px rgba(244, 114, 22, 0.3))'
            }}>
              📊
            </Box>
          </motion.div>
          
          <Typography 
            variant="h5" 
            sx={{ 
              color: '#5a6775', 
              textAlign: 'center',
              fontWeight: 600,
              position: 'relative',
              zIndex: 1,
              mb: 1
            }}
          >
            ROI Results Will Appear Here
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: '#8a9aa8', 
              textAlign: 'center',
              position: 'relative',
              zIndex: 1
            }}
          >
            Adjust the parameters on the left to calculate your automation ROI
          </Typography>
        </Box>
      </motion.div>
    );
  }

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key="results"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.5 }}
      >
        <Box sx={{ 
          p: 4, 
          borderRadius: 4,
          background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Decorative background */}
          <Box sx={{
            position: 'absolute',
            top: -50,
            right: -50,
            width: '150px',
            height: '150px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(244, 114, 22, 0.1) 0%, transparent 70%)',
          }} />
          
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 800, 
                mb: 4,
                background: 'linear-gradient(135deg, #231f20 0%, #db1d2b 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontFamily: '"Outfit", sans-serif',
                letterSpacing: '-0.02em'
              }}
            >
              Automation Factory ROI Summary
            </Typography>
          </motion.div>

          {/* Main Stats Grid */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12}>
              <StatCard 
                label="Annual Cost Savings"
                value={data.annualCostSavings}
                prefix="€"
                color="#f47216"
                delay={0.1}
                isLarge
              />
            </Grid>
            <Grid item xs={6}>
              <StatCard 
                label="Automation ROI"
                value={data.roi}
                suffix="%"
                color="#db1d2b"
                delay={0.2}
              />
            </Grid>
            <Grid item xs={6}>
              <StatCard 
                label="Payback Period"
                value={data.paybackPeriod}
                suffix=" mo"
                color="#5a7d9a"
                delay={0.3}
              />
            </Grid>
          </Grid>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 700, 
                mb: 2, 
                mt: 4,
                color: '#231f20',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <Box component="span" sx={{ 
                width: 4, 
                height: 24, 
                background: 'linear-gradient(180deg, #db1d2b 0%, #f47216 100%)',
                borderRadius: 2,
                display: 'inline-block'
              }} />
              Additional Metrics
            </Typography>
          </motion.div>
          
          <Box sx={{ 
            bgcolor: 'rgba(0,0,0,0.02)', 
            borderRadius: 3, 
            overflow: 'hidden',
            border: '1px solid rgba(0,0,0,0.05)'
          }}>
            <MetricRow 
              label="Time Saved Annually"
              value={`${data.timeSavedAnnually.toLocaleString()} hours`}
              delay={0.5}
            />
            <MetricRow 
              label="FTE Savings"
              value={`${data.fteSavings} FTE`}
              delay={0.6}
            />
            <MetricRow 
              label="Error Reduction Savings"
              value={`€${data.errorReductionSavings.toLocaleString()}`}
              delay={0.7}
            />
            <MetricRow 
              label="Total Implementation Cost"
              value={`€${data.totalImplementationCost.toLocaleString()}`}
              delay={0.8}
            />
          </Box>
          
          {/* Visual indicator */}
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: '100%' }}
            transition={{ duration: 1, delay: 0.9 }}
          >
            <Box sx={{ 
              mt: 4, 
              height: 6, 
              borderRadius: 3,
              background: 'linear-gradient(90deg, #db1d2b 0%, #f47216 50%, #5a7d9a 100%)',
              boxShadow: '0 2px 10px rgba(244, 114, 22, 0.3)'
            }} />
          </motion.div>
        </Box>
      </motion.div>
    </AnimatePresence>
  );
}

export default ResultsDisplay;
